import { useCallback, useEffect, useRef, useState } from 'react';
import { loadBgmPreference, pauseBgm, playBgm, saveBgmPreference, stopBgm } from '../services/pedometerBgm';

const VISIBLE_RATIO = 0.3;
/** 0.3 단일 threshold면 교차 전에 콜백이 안 와 스크롤을 한 번 더 해야 갱신되는 경우가 있음 */
const IO_THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];

/**
 * 산책 씬이 화면에 보이는 동안에만 배경음악을 재생한다.
 * 기본값은 ON이지만, 브라우저/WebView 자동재생 정책 때문에
 * 첫 사용자 제스처(탭) 이후에야 실제로 소리가 나기 시작할 수 있다.
 */
export function usePedometerBgm({ night }) {
  const sceneRef = useRef(null);
  const nightRef = useRef(night);
  const sceneVisibleRef = useRef(false);
  const pageVisibleRef = useRef(document.visibilityState !== 'hidden');
  const [enabled, setEnabled] = useState(loadBgmPreference);
  const [sceneVisible, setSceneVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== 'hidden');

  useEffect(() => {
    nightRef.current = night;
  }, [night]);

  useEffect(() => {
    sceneVisibleRef.current = sceneVisible;
  }, [sceneVisible]);

  useEffect(() => {
    pageVisibleRef.current = pageVisible;
  }, [pageVisible]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || typeof IntersectionObserver === 'undefined') {
      setSceneVisible(true);
      return undefined;
    }

    const root = document.getElementsByClassName('page-container')[0] || null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setSceneVisible(entry.intersectionRatio >= VISIBLE_RATIO);
      },
      { root, threshold: IO_THRESHOLDS },
    );
    observer.observe(scene);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => setPageVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    if (enabled && sceneVisible && pageVisible) {
      playBgm(night);
    } else {
      pauseBgm();
    }
  }, [enabled, night, pageVisible, sceneVisible]);

  // 자동재생이 막힌 경우: 화면 어디든 첫 탭에서 재생 시도 (기본 ON UX)
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const unlock = () => {
      if (!enabled || !sceneVisibleRef.current || !pageVisibleRef.current) return;
      playBgm(nightRef.current);
    };

    document.addEventListener('pointerdown', unlock, { capture: true });
    document.addEventListener('touchstart', unlock, { capture: true });
    document.addEventListener('click', unlock, { capture: true });

    return () => {
      document.removeEventListener('pointerdown', unlock, { capture: true });
      document.removeEventListener('touchstart', unlock, { capture: true });
      document.removeEventListener('click', unlock, { capture: true });
    };
  }, [enabled]);

  useEffect(() => () => stopBgm(), []);

  const toggle = useCallback(() => {
    setEnabled((current) => {
      const next = !current;
      saveBgmPreference(next);
      if (next && sceneVisibleRef.current && pageVisibleRef.current) {
        // 브라우저가 제스처를 확인할 수 있도록 탭 처리 중에 바로 재생한다
        playBgm(night);
      } else {
        pauseBgm();
      }
      return next;
    });
  }, [night]);

  return { sceneRef, sceneVisible, pageVisible, bgmOn: enabled, toggleBgm: toggle };
}
